import { GoalConfigCard } from "@/cards/GoalConfigCard";
import { MultiStepGoalConfigCard } from "@/cards/MultiStepGoalConfigCard";
import { MultiStepGoalWidget } from "@/Components/MultiStepGoalWidget/MultiStepGoalWidget";
import { SimpleDonoGoalWidget } from "@/Components/SimpleDonoGoalWidget/SimpleDonoGoalWidget";
import { useSocket } from "@/hooks/useSocket";
import {
    AnyGoalDoc,
    NewGoalUpdateEvent,
    NewMultiStepGoalDoc,
    NewMultiStepGoalStep,
    NewSimpleGoalDoc,
} from "@ailenax-overlay/shared";
import { FC, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

export const Goal: FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const socket = useSocket();
    const [goal, setGoal] = useState<AnyGoalDoc | null>(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        socket.emit("get-new-goals", gs => {
            const found = gs.find(bar => bar._id === id);
            if (found) {
                setGoal(found);
            }
        });

        const onNewGoalUpdate = (e: NewGoalUpdateEvent) => {
            if (e.goal._id === id) {
                setGoal(e.goal);
            }
        };

        socket.on("new-goal-update", onNewGoalUpdate);

        return () => {
            socket.off("new-goal-update", onNewGoalUpdate);
        };
    }, [id, socket]);

    const setSimpleField = <G extends NewSimpleGoalDoc, F extends keyof G>(field: F, value: G[F]) => {
        setGoal((prev) => {
            if (!prev || prev.goalType !== "simple") return prev;
            return { ...prev, [field]: value } as NewSimpleGoalDoc;
        });
    };

    const setMultiStepField = <G extends NewMultiStepGoalDoc, F extends keyof G>(field: F, value: G[F]) => {
        setGoal((prev) => {
            if (!prev || prev.goalType !== "multistep") return prev;
            return { ...prev, [field]: value } as NewMultiStepGoalDoc;
        });
    };

    const setSteps = (steps: NewMultiStepGoalStep[]) => {
        setGoal((prev) => {
            if (!prev || prev.goalType !== "multistep") return prev;
            return { ...prev, steps } as NewMultiStepGoalDoc;
        });
    };

    const doSave = useCallback(() => {
        if (!goal) return;
        socket.emit("save-new-goal", goal);
    }, [socket, goal]);

    const resetSimple = useCallback(
        (modulo: boolean = false) => {
            if (goal?.goalType !== "simple") return;
            const resetTo = modulo ? goal.progress % goal.goal : 0;
            socket.emit("save-new-goal", { ...goal, progress: resetTo });
        },
        [socket, goal]
    );

    const resetMultiStep = useCallback(() => {
        if (goal?.goalType !== "multistep") return;
        socket.emit("save-new-goal", { ...goal, progress: 0 });
    }, [socket, goal]);

    const doDelete = useCallback(() => {
        if (!goal?._id) return;
        const confirmed = confirm(
            "Are you sure you want to delete this goal? This cannot be undone."
        );
        if (!confirmed) return;

        setDeleting(true);
        socket.emit("delete-new-goal", String(goal._id), (success) => {
            setDeleting(false);
            if (success) {
                navigate("/goals/new");
            } else {
                alert("Failed to delete goal.");
            }
        });
    }, [socket, goal, navigate]);

    if (!goal) {
        return null;
    }

    return (
        <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12">
                <div className="min-w-3xl max-w-max m-auto">
                    {goal.goalType === "simple" && <SimpleDonoGoalWidget goal={goal} />}
                    {goal.goalType === "multistep" && <MultiStepGoalWidget goal={goal} />}
                </div>
            </div>
            {goal.goalType === "simple" && (
                <GoalConfigCard goal={goal} setField={setSimpleField} doSave={doSave} reset={resetSimple} />
            )}
            {goal.goalType === "multistep" && (
                <MultiStepGoalConfigCard
                    goal={goal}
                    setField={setMultiStepField}
                    setSteps={setSteps}
                    doSave={doSave}
                    reset={resetMultiStep}
                />
            )}
            <div className="col-span-12 flex justify-center">
                <button
                    type="button"
                    disabled={deleting}
                    className="text-white bg-red-900 hover:bg-red-950 disabled:opacity-50 focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm px-5 py-2.5 dark:bg-red-950 dark:hover:bg-red-900"
                    onClick={doDelete}
                >
                    {deleting ? "Deleting…" : "Delete goal"}
                </button>
            </div>
        </div>
    );
};
